import React, { createContext, useContext } from "react";
import useAccountInfo, {
  CompanyAccountResponseShim,
} from "../../../hooks/useAccountInfo";
import { useFeatureFlag } from "configcat-react";

const CREDITS_WARNING_THRESHOLD = 0.85;

type PaywallContextType = {
  routeOptimizationCreditsUsed: boolean;
  routeOptimizationCreditsWarning: string;
  siteMapCreditsUsed: boolean;
  siteMapCreditsWarning: string;
  isLoading: boolean;
};

const PaywallContext = createContext<PaywallContextType | null>(null);

export const usePaywall = () => {
  const context = useContext(PaywallContext);
  let { value: enableEnforcement, loading: featureFlagsloading } =
    useFeatureFlag("enforcePaywallForOptimizationsAndMaps", true);
  if (!context) {
    throw new Error("usePaywall must be used within a PaywallProvider");
  }
  if (featureFlagsloading) {
    return {
      ...context,
      isLoading: true,
    };
  }
  if (enableEnforcement === false) {
    return {
      ...context,
      routeOptimizationCreditsUsed: false,
      routeOptimizationCreditsWarning: "",
      siteMapCreditsUsed: false,
      siteMapCreditsWarning: "",
      isLoading: false,
    };
  }
  return context;
};

function derivePaywall(
  data: CompanyAccountResponseShim | undefined,
  jobId?: string,
): Omit<PaywallContextType, "isLoading"> {
  const siteMapsUsed = data?.account.usage.siteMaps ?? 0;
  const siteMapsTotal = data?.account.allowances.siteMaps ?? 0;
  const routeOptUsed = data?.account.usage.optimizations ?? 0;
  const routeOptTotal = data?.account.allowances.optimizations ?? 0;

  // If this job has already been edited this month it doesn't consume an additional slot,
  // so suppress the warning and the credits-used block for that job.
  const jobAlreadyEdited =
    jobId != null &&
    (data?.account.usage.siteMapsEditedThisMonth ?? []).some(
      (id) => id.toString() === jobId,
    );

  // we want to warn folks on the low plans immediately, since they don't have many credits to play with
  // for paid plans, give them some notice but they won't hit it right away
  const showSiteMapCreditsWarning =
    !jobAlreadyEdited &&
    (siteMapsTotal < 10
      ? true
      : siteMapsUsed >= siteMapsTotal * CREDITS_WARNING_THRESHOLD);

  const siteMapCreditsUsed =
    !jobAlreadyEdited && data != null && siteMapsUsed >= siteMapsTotal;
  const siteMapCreditsWarning =
    data != null && showSiteMapCreditsWarning
      ? `${siteMapsUsed} of ${siteMapsTotal} map editing credits used`
      : "";

  // we want to warn folks on the low plans immediately, since they don't have many credits to play with
  // for paid plans, give them some notice but they won't hit it right away
  const showRouteOptimizationCreditsWarning =
    routeOptTotal < 10
      ? true
      : routeOptUsed >= routeOptTotal * CREDITS_WARNING_THRESHOLD;

  const routeOptimizationCreditsUsed =
    data != null && routeOptUsed >= routeOptTotal;
  const routeOptimizationCreditsWarning =
    data != null && showRouteOptimizationCreditsWarning
      ? `${routeOptUsed} of ${routeOptTotal} optimizing credits used`
      : "";

  return {
    siteMapCreditsUsed: siteMapCreditsUsed,
    siteMapCreditsWarning: siteMapCreditsWarning,
    routeOptimizationCreditsUsed: routeOptimizationCreditsUsed,
    routeOptimizationCreditsWarning: routeOptimizationCreditsWarning,
  };
}

export const PaywallProvider = ({
  children,
  jobId,
}: {
  children: React.ReactNode;
  jobId?: string;
}) => {
  const { data, isLoading } = useAccountInfo();

  return (
    <PaywallContext.Provider
      value={{ isLoading, ...derivePaywall(data, jobId) }}
    >
      {children}
    </PaywallContext.Provider>
  );
};

export default PaywallProvider;

// A simple dummy context that always shows the account as ok. Used for rendering site maps from the backend.
export const PaywallProviderSkip = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <PaywallContext.Provider
      value={{
        siteMapCreditsUsed: false,
        siteMapCreditsWarning: "",
        routeOptimizationCreditsUsed: false,
        routeOptimizationCreditsWarning: "",
        isLoading: false,
      }}
    >
      {children}
    </PaywallContext.Provider>
  );
};
